import { DefinitionNode, TypeSystemExtensionNode, parse } from 'graphql';
import _ from 'lodash';

import { assertUnreachable } from './assertUnreachable';

export type DefinitionLookup = {
  types: Map<string, Exclude<DefinitionNode, TypeSystemExtensionNode>>;
  directives: Map<string, Exclude<DefinitionNode, TypeSystemExtensionNode>>;
  schema: Exclude<DefinitionNode, TypeSystemExtensionNode> | null;
};
export function parseGraphqlMergedDefinitions(
  source: string,
): DefinitionLookup {
  return (
    parse(source)
      .definitions.slice()
      // Sort definitions so extension always come last, this means we can create
      // the definition if we encounter an extension that doesn't exist yet
      .sort(
        (a, b) =>
          Number(a.kind.endsWith('Extension')) -
          Number(b.kind.endsWith('Extension')),
      )
      .reduce(
        (acc: DefinitionLookup, nextDefinition) => {
          const existingType =
            'name' in nextDefinition && nextDefinition.name
              ? acc.types.get(nextDefinition.name.value)
              : null;

          switch (nextDefinition.kind) {
            case 'EnumTypeExtension':
            case 'EnumTypeDefinition': {
              if (existingType && existingType.kind !== 'EnumTypeDefinition') {
                throw new Error('extension type mismatch');
              }

              acc.types.set(nextDefinition.name.value, {
                ...existingType,
                ...nextDefinition,
                kind: 'EnumTypeDefinition',
                directives: [
                  ...(existingType?.directives ?? []),
                  ...(nextDefinition.directives ?? []),
                ],
                values: _.orderBy(
                  [
                    ...(existingType?.values ?? []),
                    ...(nextDefinition.values ?? []),
                  ],
                  'name.value',
                ),
              });
              break;
            }
            case 'SchemaExtension':
            case 'SchemaDefinition':
              if (acc.schema && acc.schema.kind !== 'SchemaDefinition') {
                throw new Error('extension type mismatch');
              }

              acc.schema = {
                ...acc.schema,
                ...nextDefinition,
                kind: 'SchemaDefinition',
                directives: [
                  ...(acc.schema?.directives ?? []),
                  ...(nextDefinition.directives ?? []),
                ],
                operationTypes: _.orderBy(
                  [
                    ...(acc.schema?.operationTypes ?? []),
                    ...(nextDefinition.operationTypes ?? []),
                  ],
                  'operation',
                ),
              };
              break;
            case 'UnionTypeExtension':
            case 'UnionTypeDefinition':
              if (existingType && existingType.kind !== 'UnionTypeDefinition') {
                throw new Error('extension type mismatch');
              }

              acc.types.set(nextDefinition.name.value, {
                ...existingType,
                ...nextDefinition,
                kind: 'UnionTypeDefinition',
                directives: [
                  ...(existingType?.directives ?? []),
                  ...(nextDefinition.directives ?? []),
                ],
                types: _.orderBy(
                  [
                    ...(existingType?.types ?? []),
                    ...(nextDefinition.types ?? []),
                  ],
                  'name.value',
                ),
              });
              break;
            case 'ObjectTypeExtension':
            case 'ObjectTypeDefinition':
              if (
                existingType &&
                existingType.kind !== 'ObjectTypeDefinition'
              ) {
                throw new Error('extension type mismatch');
              }

              acc.types.set(nextDefinition.name.value, {
                ...existingType,
                ...nextDefinition,
                kind: 'ObjectTypeDefinition',
                directives: [
                  ...(existingType?.directives ?? []),
                  ...(nextDefinition.directives ?? []),
                ],
                fields: _.orderBy(
                  [
                    ...(existingType?.fields ?? []),
                    ...(nextDefinition.fields ?? []),
                  ],
                  'name.value',
                ),
                interfaces: _.orderBy(
                  [
                    ...(existingType?.interfaces ?? []),
                    ...(nextDefinition.interfaces ?? []),
                  ],
                  'name.value',
                ),
              });
              break;
            case 'ScalarTypeExtension':
            case 'ScalarTypeDefinition':
              if (
                existingType &&
                existingType.kind !== 'ScalarTypeDefinition'
              ) {
                throw new Error('extension type mismatch');
              }

              acc.types.set(nextDefinition.name.value, {
                ...existingType,
                ...nextDefinition,
                kind: 'ScalarTypeDefinition',
                directives: [
                  ...(existingType?.directives ?? []),
                  ...(nextDefinition.directives ?? []),
                ],
              });
              break;
            case 'InterfaceTypeExtension':
            case 'InterfaceTypeDefinition':
              if (
                existingType &&
                existingType.kind !== 'InterfaceTypeDefinition'
              ) {
                throw new Error('extension type mismatch');
              }

              acc.types.set(nextDefinition.name.value, {
                ...existingType,
                ...nextDefinition,
                kind: 'InterfaceTypeDefinition',
                directives: [
                  ...(existingType?.directives ?? []),
                  ...(nextDefinition.directives ?? []),
                ],
                fields: _.orderBy(
                  [
                    ...(existingType?.fields ?? []),
                    ...(nextDefinition.fields ?? []),
                  ],
                  'name.value',
                ),
                interfaces: _.orderBy(
                  [
                    ...(existingType?.interfaces ?? []),
                    ...(nextDefinition.interfaces ?? []),
                  ],
                  'name.value',
                ),
              });
              break;
            case 'InputObjectTypeExtension':
            case 'InputObjectTypeDefinition':
              if (
                existingType &&
                existingType.kind !== 'InputObjectTypeDefinition'
              ) {
                throw new Error('extension type mismatch');
              }

              acc.types.set(nextDefinition.name.value, {
                ...existingType,
                ...nextDefinition,
                kind: 'InputObjectTypeDefinition',
                directives: [
                  ...(existingType?.directives ?? []),
                  ...(nextDefinition.directives ?? []),
                ],
                fields: _.orderBy(
                  [
                    ...(existingType?.fields ?? []),
                    ...(nextDefinition.fields ?? []),
                  ],
                  'name.value',
                ),
              });
              break;
            case 'OperationDefinition':
            case 'FragmentDefinition':
              throw new Error(
                'schemas cannot contain operation or fragment definitions',
              );
            case 'DirectiveDefinition':
              // directives cannot be extended, so we don't need to merge
              acc.directives.set(nextDefinition.name.value, nextDefinition);
              break;
            default:
              assertUnreachable(nextDefinition);
          }
          return acc;
        },
        { types: new Map(), directives: new Map(), schema: null },
      )
  );
}
